import apiClient from './apiClient';
import type { LogEntry, LogsMetrics, SectionTimeline, UserTimeline, EnhancedSectionTimeline, PaginatedEnhancedSectionTimeline, GroupedOperationsResponse } from '../types';

// Tipos para o export com polling
export interface ExportJobStatus {
  jobId: string;
  status: 'pending' | 'running' | 'completed' | 'error';
  phase: string;
  progress: number;
  totalOperations: number;
  processedOperations: number;
  errorMessage?: string;
  filename?: string;
  createdAt: string;
  completedAt?: string;
}

export interface StartExportResponse {
  jobId: string;
}

/**
 * Service para interagir com a API de Logs (Azure Log Analytics)
 */

export const logsService = {
  /**
   * Obtém métricas agregadas de logs
   */
  async getMetrics(hoursAgo: number = 24): Promise<LogsMetrics> {
    const response = await apiClient.get<LogsMetrics>('/logs/metrics', {
      params: { hoursAgo }
    });
    return response.data;
  },

  /**
   * Busca logs com filtros opcionais
   */
  async search(
    searchTerm?: string,
    maxResults: number = 100,
    dateFrom?: Date,
    dateTo?: Date
  ): Promise<LogEntry[]> {
    const response = await apiClient.get<LogEntry[]>('/logs/search', {
      params: {
        searchTerm,
        maxResults,
        startDate: dateFrom?.toISOString(),
        endDate: dateTo?.toISOString()
      }
    });
    return response.data;
  },

  /**
   * Obtém timeline de mensagens por CorrelationId
   */
  async getByCorrelationId(correlationId: string): Promise<LogEntry[]> {
    const response = await apiClient.get<LogEntry[]>(`/logs/correlation/${correlationId}`);
    return response.data;
  },

  /**
   * Obtém timeline de eventos por SectionExternalId (classe) - versão legada
   * Seção 7 - Correlação Hierárquica
   */
  async getBySectionId(sectionId: string, limit: number = 500): Promise<SectionTimeline> {
    const response = await apiClient.get<SectionTimeline>(`/logs/section/${encodeURIComponent(sectionId)}`, {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Obtém timeline enriquecida de eventos por SectionExternalId (classe)
   * Agrupa eventos por CorrelationId, inclui nome da classe e dados dos usuários
   * Seção 7 - Correlação Hierárquica v2
   */
  async getEnrichedBySectionId(sectionId: string, limit: number = 500): Promise<EnhancedSectionTimeline> {
    const response = await apiClient.get<EnhancedSectionTimeline>(`/logs/section/${encodeURIComponent(sectionId)}/enriched`, {
      params: { limit, _t: Date.now() },
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      }
    });
    return response.data;
  },

  /**
   * Obtém resumo de uma classe (sem eventos detalhados)
   */
  async getSectionSummary(sectionId: string): Promise<SectionTimeline> {
    const response = await apiClient.get<SectionTimeline>(`/logs/section/${encodeURIComponent(sectionId)}/summary`);
    return response.data;
  },

  /**
   * Obtém timeline de eventos por UserExternalId (aluno/professor)
   */
  async getByUserId(userId: string, limit: number = 100): Promise<UserTimeline> {
    const response = await apiClient.get<UserTimeline>(`/logs/user/${encodeURIComponent(userId)}`, {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Obtém timeline enriquecida paginada de eventos por SectionExternalId (classe)
   * Com filtros de data e paginação
   */
  async getEnrichedBySectionIdPaginated(
    sectionId: string,
    startDate?: Date,
    endDate?: Date,
    pageNumber: number = 1,
    pageSize: number = 50
  ): Promise<PaginatedEnhancedSectionTimeline> {
    const response = await apiClient.get<PaginatedEnhancedSectionTimeline>(
      `/logs/section/${encodeURIComponent(sectionId)}/enriched/paginated`,
      {
        params: {
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
          pageNumber,
          pageSize
        }
      }
    );
    return response.data;
  },

  /**
   * Obtém operações recentes (últimas 48 horas) para carga inicial da página
   * Endpoint otimizado sem necessidade de filtros - carrega automaticamente
   * @param pageSize Quantidade de operações por página (padrão: 50)
   * @param cursor Cursor para próxima página
   */
  async getRecentOperations(
    pageSize: number = 50,
    cursor?: string
  ): Promise<GroupedOperationsResponse> {
    const response = await apiClient.get<GroupedOperationsResponse>('/logs/operations/recent', {
      params: {
        pageSize,
        cursor: cursor || undefined
      }
    });
    return response.data;
  },

  /**
   * Obtém operações agrupadas por CorrelationId com paginação baseada em cursor
   * Ideal para listagem de operações com "Carregar mais"
   * @param startDate Data inicial do filtro
   * @param endDate Data final do filtro
   * @param cursor Cursor para próxima página
   * @param searchTerm Termo de busca por nome (busca parcial)
   * @param pageSize Quantidade de operações por página
   * @param externalId ID externo para filtro exato (stu_, tea_, etc)
   * @param messageType Tipo de entidade (Student, Teacher, etc)
   * @param operation Operação específica (NewStudent, UpdateStudent, etc)
   * @param status Status da operação (success, error, warning, processing)
   */
  async getGroupedOperations(
    startDate?: Date,
    endDate?: Date,
    cursor?: string,
    searchTerm?: string,
    pageSize: number = 50,
    externalId?: string,
    messageType?: string,
    operation?: string,
    status?: string
  ): Promise<GroupedOperationsResponse> {
    const response = await apiClient.get<GroupedOperationsResponse>('/logs/operations/grouped', {
      params: {
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        cursor,
        searchTerm: searchTerm || undefined,
        externalId: externalId || undefined,
        messageType: messageType || undefined,
        operation: operation || undefined,
        status: status || undefined,
        pageSize
      }
    });
    return response.data;
  },

  /**
   * Exporta operacoes para CSV (processamento no backend) - DEPRECADO
   * Use startExportJob + polling para exports grandes
   */
  async exportOperationsToCsv(
    startDate?: Date,
    endDate?: Date,
    searchTerm?: string,
    externalId?: string,
    messageType?: string,
    operation?: string,
    status?: string
  ): Promise<Blob> {
    const response = await apiClient.get('/logs/operations/export', {
      params: {
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        searchTerm: searchTerm || undefined,
        externalId: externalId || undefined,
        messageType: messageType || undefined,
        operation: operation || undefined,
        status: status || undefined
      },
      responseType: 'blob',
      timeout: 600000 // 10 minutos para exports grandes
    });
    return response.data;
  },

  /**
   * Inicia job de exportacao assincrona (recomendado para exports grandes)
   * Retorna jobId para acompanhar via polling
   */
  async startExportJob(
    startDate?: Date,
    endDate?: Date,
    searchTerm?: string,
    externalId?: string,
    messageType?: string,
    operation?: string,
    status?: string
  ): Promise<StartExportResponse> {
    const response = await apiClient.post<StartExportResponse>('/logs/operations/export/start', null, {
      params: {
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        searchTerm: searchTerm || undefined,
        externalId: externalId || undefined,
        messageType: messageType || undefined,
        operation: operation || undefined,
        status: status || undefined
      }
    });
    return response.data;
  },

  /**
   * Consulta status de um job de exportacao
   */
  async getExportJobStatus(jobId: string): Promise<ExportJobStatus> {
    const response = await apiClient.get<ExportJobStatus>(`/logs/operations/export/status/${jobId}`);
    return response.data;
  },

  /**
   * Baixa arquivo CSV de um job concluido
   */
  async downloadExportJob(jobId: string): Promise<Blob> {
    const response = await apiClient.get(`/logs/operations/export/download/${jobId}`, {
      responseType: 'blob'
    });
    return response.data;
  }
};
